import { IJobMeta } from '../../utils/BackgroundJobs';
import { useDispatch } from 'react-redux';
import React, { useCallback, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import CheckJobStatusQuery from '../../graphql/queries/system/CheckJobStatusQuery.graphql';
import { appActions } from '../../state/modules/app';
import { AlertType, ICheckJobStatusQueryResult, ICheckJobStatusQueryVariables } from '../../graphql/schema/p4types';
import Notification, { INotificationProps } from './Notification';
import { useP4ToastController } from '../../views/system/NotificationsArea';

const useJobHandler = (dismiss: () => void) => {
  const dispatch = useDispatch();
  const { dispatchSimpleToast } = useP4ToastController();

  return useCallback(
    (job: IJobMeta) => {
      dispatch(appActions.updateJobStatus(job));

      if (job && job.complete) {
        if (!job.clientMutationId) {
          let alertText;
          let alertType;
          if (job.recordsFailed && job.recordsFailed > 0) {
            alertText = `Job completed with ${job.recordsFailed} failure(s)`;
            alertType = AlertType.Warning;
          } else {
            alertText = 'Job completed';
            alertType = AlertType.Success;
          }

          if (job.errorMessages && job.errorMessages.length > 0) {
            alertText += `\n${job.errorMessages.join('\n')}`;
          }

          if (job.message) {
            dispatchSimpleToast(`${job.title}`, job.message, AlertType.Info);
          }

          dispatchSimpleToast(`${job.title}`, alertText, alertType);
          dismiss();
          dispatch(appActions.completeJob(job));
          //job.onComplete called last as it may error or cause other issues that then cause the job to get stuck in a loop.
          if (job.onComplete) {
            job.onComplete(job);
          }
        } else {
          dismiss();
          dispatch(appActions.completeJob(job));
        }
      } else if (job && job.failed) {
        if (!job.onFailed?.(job)) {
          dispatchSimpleToast('Job could not be completed', job.message ? job.message : '', AlertType.Error);
        }
        //Dismiss the alert so it doesn't continue to poll for updates after failure.
        dismiss();
        dispatch(appActions.failJob(job));
      }
    },
    [dismiss, dispatch, dispatchSimpleToast]
  );
};

export const JobNotification = ({
  title,
  message,
  type,
  job,
  dismiss,
}: INotificationProps & { job: IJobMeta; dismiss: () => void }) => {
  const [dismissed, setDismissed] = useState(false);
  const jobHandler = useJobHandler(() => {
    //While the dismiss 'will' remove the job toast and therefore stop polling, an in-flight request may still complete unless we
    //explicitly set the skip flag, which can cause 'onCompleteJob' logic to run twice. Thus, we track local dismissed state to block that scenario.
    setDismissed(true);
    dismiss();
  });

  const updateCounter = useRef(0);

  let pollInterval: number;
  if (dismissed) {
    pollInterval = 0;
  } else {
    pollInterval = updateCounter.current < 15 ? 1250 : 2500;
  }

  const { dispatchSimpleToast } = useP4ToastController();

  const { data } = useQuery<ICheckJobStatusQueryResult, ICheckJobStatusQueryVariables>(CheckJobStatusQuery, {
    variables: { jobId: job.jobId },
    pollInterval: pollInterval, //Slow down updates on long running jobs.
    onCompleted: (data) => {
      updateCounter.current++;
      const jobStatus = data?.jobStatus;

      if (jobStatus && job.jobId) {
        jobHandler({ ...job, ...jobStatus });
      } else {
        dispatchSimpleToast('Job Status Check', 'Job status timeout - Action may still be running.', AlertType.Error);
        if (updateCounter.current > 30) {
          //If job has been running for a while and we get no status, dismiss the notification so we don't keep spamming status requests.
          //(Waits for a while to ensure we don't accidentally clear immediately, i.e. if the first job status update failed for any reason).
          dismiss();
        }
      }
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: dismissed,
  });

  return (
    <Notification
      title={title}
      message={data?.jobStatus?.message ?? message}
      type={type}
      job={data?.jobStatus ?? job}
    />
  );
};
